import { Component, OnInit, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { MatPaginator, MatSort, MatBottomSheet, MatTable } from '@angular/material';
import { TableEventsDataSource } from './table-events-datasource';
import { Observable, Subscription } from 'rxjs';
import { EventService } from '../../services/event.service';
import { Event } from '../../models/event';
import { ViewEncapsulation } from '@angular/core';
import { StoreSelectorService } from '../../services/storeselector.service';
import { StoreSelection, Store } from '../../models/store';
import { StoreSelectorComponent } from '../store-selector/store-selector.component';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { Router, RoutesRecognized, NavigationEnd } from '@angular/router';
import { DatepickerUpdate, TimeFilterUpdate, ControlsComponent } from './controls/controls.component';
import { AuthService } from '../../services/auth.service';
import { StoreService } from 'src/app/services/store.service';
import { pairwise, filter } from 'rxjs/operators';
import { SecurityEventComponent } from '../security-event/security-event.component';

const DISPLAYED_COLUMNS_XSMALL: string[] = ['timeStamp', 'entranceName'];
const DISPLAYED_COLUMNS_SMALL: string[] = ['hasVideo', 'timeStamp', 'entranceName'];
const DISPLAYED_COLUMNS_MEDIUM: string[] = ['hasVideo', 'timeStamp', 'entranceName'];

const WARNING_NO_STORE: string = "No store selected.";
const WARNING_NO_EVENTS: string = `No events found`;

@Component({
    selector: 'table-events',
    host: {
        "[style.display]": "'block'",
    },
    templateUrl: './table-events.component.html',
    styleUrls: ['./table-events.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class TableEventsComponent implements OnInit, OnDestroy {
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    dataSource: TableEventsDataSource;

    @ViewChild(ControlsComponent) controls: ControlsComponent;
    @ViewChild(MatTable) table: any;

    selectedStore: StoreSelection;
    stores: Store[] = [];

    selectedRow: any;

    defaultPageSize = 50;
    isLoadingResults = true;
    hasError = false;
    hasWarning = false;
    errorMessage = "";
    warningMessage = "";

    fromDate: Date;
    toDate: Date;
    initDate: boolean = false;

    isSmallLayout: boolean = true;

    tableScrollPosition: number = 0;

    tableEventsPath: string;
    securityEventPath: string;

    /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
    displayedColumns = DISPLAYED_COLUMNS_MEDIUM;

    private selectedStoreChangesSubscription: Subscription;

    constructor(private _authService: AuthService,
        private _eventService: EventService,
        private _storeSelectorService: StoreSelectorService,
        private storeSelector: MatBottomSheet,
        private router: Router,
        private breakpointObserver: BreakpointObserver,
        private _storeService: StoreService) { }

    ngOnInit() {
        /* Data Source */
        this.dataSource = new TableEventsDataSource(this.paginator, this.sort);

        this.getStores();

        /* Selected Store */
        // Get initial selected store
        let initSelectedStore: StoreSelection = this._storeSelectorService.getSelectedStore();
        this.onStoreChange(initSelectedStore);

        // Update Selected Store UI when selected store changes
        this.selectedStoreChangesSubscription = this._storeSelectorService.storeChanges.subscribe((newSelectedStore: StoreSelection) => {
            this.onStoreChange(newSelectedStore);
        });

        /* Table Columns Breakpoints */
        // Change Columns on XSmall Breakpoint
        this.breakpointObserver
            .observe([Breakpoints.XSmall])
            .subscribe((state: BreakpointState) => {
                if (state.matches) {
                    // change displayed columns
                    this.displayedColumns = DISPLAYED_COLUMNS_XSMALL;
                }
            });
        // Change Columns on Small Breakpoint
        this.breakpointObserver
            .observe([Breakpoints.Small])
            .subscribe((state: BreakpointState) => {
                if (state.matches) {
                    // change displayed columns
                    this.displayedColumns = DISPLAYED_COLUMNS_SMALL;
                }
            });
        // Change Columns on Medium or Larger Breakpoint
        this.breakpointObserver
            .observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
            .subscribe((state: BreakpointState) => {
                if (state.matches) {
                    // change displayed columns
                    this.displayedColumns = DISPLAYED_COLUMNS_MEDIUM;
                }
            });

        this.tableEventsPath = this.router.config.find(e => e.component == TableEventsComponent).path;
        this.securityEventPath = this.router.config.find(e => e.component == SecurityEventComponent).path.split('/')[0];

        this.router.events.pipe(filter((e: any) => e instanceof NavigationEnd), pairwise()).subscribe((e: NavigationEnd[]) => {
            const url = e[1].urlAfterRedirects.replace('/', '');
            const prevUrl = e[0].urlAfterRedirects.replace('/', '');

            if (url.startsWith(this.tableEventsPath) && prevUrl.startsWith(this.securityEventPath)) {
                // Restore scroll position on events table
                this.setTableScrollPos(this.tableScrollPosition);
            }

        })
    }

    private setTableScrollPos(scrollPos: number) {
        this.table._elementRef.nativeElement.scrollTop = scrollPos;
    }

    getStores() {
        this._storeService.getStores().then((storeRequest: Observable<Store[]>) => {
            storeRequest.subscribe((stores: Store[]) => {
                this.stores = stores;
            }, err => {
                console.error(err);
                this.stores = [];
            })
        })
    }

    ngOnDestroy() {
        this.selectedStoreChangesSubscription.unsubscribe();
    }

    private onStoreChange(newSelectedStore: StoreSelection) {
        this.selectedStore = newSelectedStore;
        // Delete cache of data
        this.dataSource.data = [];
        this.resetPaginator();
        this.getEvents(0, this.paginator.pageSize || this.defaultPageSize);
    }

    // Opens a Store Selector with no restrictions
    private openStoreSelector(): void {
        setTimeout(() => {
            this.storeSelector.open(StoreSelectorComponent);
        })
    }

    // Returns true if valid, false if invalid
    private isSelectedStoreValid(): boolean {
        if (this.selectedStore === null) {
            return false;
        }
        return true;
    }

    // Get events and add them to datasource
    getEvents(skip: number, take: number) {
        if (!this.initDate) {
            return;
        }
        if (!this.isSelectedStoreValid()) {
            // Display warning that there is no store selected
            this.isLoadingResults = false;
            this.warningMessage = WARNING_NO_STORE;
            this.hasWarning = true;
            this.hasError = false;
            // No store, Show no data
            this.dataSource.data = [];
            return;
        }

        this.isLoadingResults = true;
        this.hasError = false;
        this.hasWarning = false;

        let sortField = this.dataSource.sortProperty;
        let sortOrder = this.dataSource.sortOrder;
        // Loop through all events from the server
        this._eventService.getEventsTimeStamp(this.selectedStore.storeGuid, this.fromDate, this.toDate, skip, take, sortField, sortOrder).then((eventRequest: Observable<any>) => {
            eventRequest.subscribe((eventData) => {
                var events = eventData.history;
                // Check if there were events
                if (events.length > 0) {
                    this.dataSource.data = events;
                    setTimeout(() => {
                        this.dataSource.totalCount = eventData.count;
                    }, 0);
                    this.hasWarning = false;
                }
                // If there were no events, display a warning
                else {
                    const noEvents = [];
                    this.dataSource.totalCount = 0;
                    this.dataSource.data = noEvents;
                    this.hasWarning = true;
                    this.warningMessage = WARNING_NO_EVENTS;

                    // console.log("warning: ", this.warningMessage);
                }

                // On success, Disable loading bar
                this.isLoadingResults = false;
                // On success, Disable error message
                this.hasError = false;

            }, err => {
                // On error, Show no data
                // this.dataSource.data = [];
                // On error, Disable loading bar
                this.isLoadingResults = false;
                // On error, Set user facing error message to error from event.service
                this.errorMessage = err;
                // On error, Show loading message
                this.hasError = true;
                this.hasWarning = false;
            });
        });
    }

    resetPaginator() {
        this.paginator.pageIndex = 0;
    }

    onPaginate($event) {
        const pageIndex = $event.pageIndex;
        const numEventsShown = (pageIndex + 1) * $event.pageSize;
        const numEventsInCache = this.dataSource.data.length;
        if (numEventsShown >= numEventsInCache) {
            // Get more events using oldest event date as the toDate for next request
            this.getEvents($event.pageSize * pageIndex, $event.pageSize);
        }
        // Reset table scroll position to top when user clicks paginate button
        this.tableScrollPosition = 0
        this.setTableScrollPos(this.tableScrollPosition);
    }

    updateSort() {
        this.getEvents(this.paginator.pageSize * this.paginator.pageIndex, this.paginator.pageSize);
    }

    // When clicking on an event row, navigate to event page
    onEventClick(row) {
        // Save table scroll pos
        this.tableScrollPosition = this.table._elementRef.nativeElement.scrollTop;

        this.selectedRow = row;

        let rowIndex = this.dataSource.data.indexOf(this.selectedRow);
        let event: Event = this.dataSource.data[rowIndex];

        // Show videos from event
        this.router.navigate(['/event', event.eventGuid]);
    }

    onRangeDateSelected(dateUpdate: DatepickerUpdate) {
        this.fromDate = dateUpdate.fromDate;
        this.toDate = dateUpdate.toDate;
        this.paginator.pageIndex = 0;
        this.initDate = true;
        this.getEvents(0, this.paginator.pageSize || this.defaultPageSize);
    }

    onTimeFilterUpdate(timeFilterUpdate: TimeFilterUpdate) {
        this.resetPaginator();
        // returns null if the filter has been disabled
        // console.log("table-events: timeFilterUpdate", timeFilterUpdate);
        this.dataSource.filter = timeFilterUpdate;
    }

    // Change the auto renew update interval in controls.component.ts
    onAutoRenewUpdate(dateUpdate: DatepickerUpdate) {
        let sortField = this.dataSource.sortProperty;
        let sortOrder = this.dataSource.sortOrder;
        this._eventService.getEventsTimeStamp(this.selectedStore.storeGuid, dateUpdate.fromDate, dateUpdate.toDate, this.paginator.pageIndex * this.paginator.pageSize, this.paginator.pageSize, sortField, sortOrder).then((eventRequest: Observable<any>) => {
            eventRequest.subscribe((eventData: any) => {

                let events = eventData.history;
                let autorenewEvents = events;

                // Remove any duplicates
                let filtered = autorenewEvents.filter(this.filterAutoRenewResults, this);

                // console.log("autorenew result: ", autorenewEvents, "filtered count: ", filtered.length, filtered);

                // Add the filtered events to the table datasource
                let data = this.dataSource.data;
                filtered.push(...data);
                this.dataSource.data = filtered;
            });
        });
    }

    filterAutoRenewResults(event: Event) {
        // Try to find event by eventGuid in the dataset.
        // If it exists, return false. We don't want to add duplicates.
        // If it does not exist, return true. We want to add it to the dataset.
        return !this.dataSource.data.find((e) => {
            return e.eventGuid == event.eventGuid;
        });
    }

    onLayoutUpdate(isSmallLayout: boolean) {
        this.isSmallLayout = isSmallLayout;
    }

    onRefreshClick() {
        this.getEvents(0, this.paginator.pageSize);
    }

    logout(): void {
        this._authService.Logoff();
    }
}
